import { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';

import { supabase } from '@/lib/supabase';
import AnnouncementBanner, { BannerSettings } from '@/components/ui/AnnouncementBanner';
import AnimatedRoutes from './components/AnimatedRoutes';
import { AuthProvider, useAuth } from './lib/authContext';
import SEO from '@/components/SEO';
import { ThemeProvider } from './lib/ThemeContext';

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
  </div>
);

// Global Auth Interceptor to handle invitations and redirects
function AuthInterceptor() {
  const navigate = useNavigate();
  const { session, profile, profileError, loading } = useAuth();

  useEffect(() => {
    const path = window.location.pathname;
    const role = profile?.role;

    if (import.meta.env.DEV) console.log('[AUTH-INTERCEPTOR] Check:', {
      loading,
      hasSession: !!session,
      hasProfile: !!profile,
      role,
      path,
      profileError: !!profileError
    });

    // Don't redirect while still loading auth state
    if (loading) {
      if (import.meta.env.DEV) console.log('[AUTH-INTERCEPTOR] Still loading, skipping redirect');
      return;
    }

    // Case 1: Authenticated with valid profile
    if (session?.user && profile) {
      if (import.meta.env.DEV) console.log('[AUTH-INTERCEPTOR] Case 1: Has session and profile');
      if (role === 'ADMIN') {
        // Admin users: redirect login pages to admin dashboard
        if (location.pathname === '/login' || location.pathname === '/admin/login' || location.pathname === '/portal/login') {
          if (import.meta.env.DEV) console.log('[AUTH-INTERCEPTOR] Redirecting ADMIN to admin dashboard');
          navigate('/admin/dashboard');
        }
      } else if (role === 'AUTHORITY_USER') {
        // Authority users: redirect login pages to portal dashboard
        if (location.pathname === '/login' || location.pathname === '/admin/login' || location.pathname === '/portal/login') {
          if (import.meta.env.DEV) console.log('[AUTH-INTERCEPTOR] Redirecting AUTHORITY_USER to portal dashboard');
          navigate('/portal/dashboard');
        }
        // Authority users accessing /admin/* should go to portal
        if (path.startsWith('/admin') && path !== '/admin/setup-password') {
          if (import.meta.env.DEV) console.log('[AUTH-INTERCEPTOR] Redirecting AUTHORITY_USER from admin to portal');
          navigate('/portal/dashboard');
        }
      } else if (role === 'USER') {
        // Regular users cannot access admin or portal area
        if ((path.startsWith('/admin') && path !== '/admin/setup-password') || path.startsWith('/portal')) {
          if (import.meta.env.DEV) console.log('[AUTH-INTERCEPTOR] Redirecting USER away from admin/portal');
          navigate('/');
        }
      }
    }
    // Case 2: Has session but profile failed to load
    else if (session?.user && profileError && !profile) {
      if (import.meta.env.DEV) console.log('[AUTH-INTERCEPTOR] Profile error - letting guard handle');
    }
    // Case 3: No session — redirect from protected routes
    else if (!session) {
      if (path.startsWith('/admin') && path !== '/admin/login' && path !== '/admin/setup-password') {
        if (import.meta.env.DEV) console.log('[AUTH-INTERCEPTOR] No session, redirecting to admin login');
        navigate('/admin/login');
      }
      if (path.startsWith('/portal') && path !== '/portal/login') {
        if (import.meta.env.DEV) console.log('[AUTH-INTERCEPTOR] No session, redirecting to portal login');
        navigate('/portal/login');
      }
    } else {
      if (import.meta.env.DEV) console.log('[AUTH-INTERCEPTOR] No action taken');
    }
  }, [session, profile, profileError, loading, navigate]);

  return null;
}

function App() {
  const [bannerSettings, setBannerSettings] = useState<BannerSettings | null>(null);

  useEffect(() => {
    // Fetch public banner settings
    const fetchBanner = async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'banner_public')
          .single();

        if (data && !error) {
          setBannerSettings(data.value as BannerSettings);
        }
      } catch (err) {
        console.error('Failed to fetch banner:', err);
      }
    };

    fetchBanner();

    // Subscribe to changes
    const channel = supabase
      .channel('public_banner_updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'system_settings',
          filter: 'key=eq.banner_public'
        },
        (payload) => {
          setBannerSettings(payload.new.value as BannerSettings);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AuthInterceptor />
            <div className="flex flex-col min-h-screen">
              <AnnouncementBanner settings={bannerSettings} />
              <SEO
                title="Forside"
                description="TipsNorge - Bidra til et tryggere nærmiljø. Rapporter hull i veien, søppel og andre problemer direkte til din kommune."
              />
              <Suspense fallback={<LoadingFallback />}>
                <AnimatedRoutes />
              </Suspense>
            </div>
          </Router>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
